import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { TRAINING_COURSES } from "../data/training_courses";
import { TRAINING_SCRIPTS } from "../data/training_scripts";

/* /training/:slug/scripts-print
   Senior-friendly, large-type, mark-up-ready printable scripts.
   Hits the browser's native Print dialog — no PDF library required.
   Each lesson starts on its own page (page-break-after on lesson cards).

   Print CSS hides the nav + buttons so what hits the paper is just the
   scripts, big print, double line spacing, with a "Notes" margin. */

export default function TrainingScriptsPrintPage() {
  const { slug } = useParams();
  const course = TRAINING_COURSES.find((c) => c.slug === slug);
  const scriptSet = TRAINING_SCRIPTS[slug];

  useEffect(() => {
    if (course) {
      document.title = `${course.title} — Academy Scripts`;
    }
  }, [course]);

  if (!course) return <Navigate to="/academy" replace />;

  const scripts = scriptSet?.lessons || {};
  const lessons = course.chapters.map((ch) => ({
    n: ch.n,
    title: ch.title,
    script: scripts[ch.n]?.script || null,
    duration: scripts[ch.n]?.durationSec || null,
  }));

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 0.6in 0.7in; size: letter; }
          body { background: white !important; }
          .script-card { page-break-after: always; break-after: page; box-shadow: none !important; border: none !important; }
          .script-card:last-child { page-break-after: auto; break-after: auto; }
        }
        body { background: #FFFFFF; }
      `}</style>

      {/* ── Toolbar (hidden on print) ── */}
      <div className="no-print sticky top-0 z-50 border-b-2 shadow-sm"
           style={{ background: "#EAF6FC", borderColor: course.accent }}>
        <div className="max-w-4xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <Link to={`/academy/${slug}`}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-white/85 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back To Course
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="print-scripts-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.03] transition"
            style={{ background: course.accent }}>
            <Printer className="w-4 h-4" /> Print / Save As PDF
          </button>
        </div>
      </div>

      {/* ── Cover page ── */}
      <div className="max-w-4xl mx-auto px-8 pt-10 pb-12">
        <div className="script-card mb-12">
          <div className="text-[11px] tracking-[0.32em] uppercase font-bold mb-3"
               style={{ color: course.accent }}>
            ★ Faitharama Academy · Recording Scripts
          </div>
          <h1 className="font-serif-display text-5xl md:text-6xl uppercase leading-[1.0] mb-3"
              style={{ color: "#0F172A", textWrap: "balance" }}>
            {course.title}
          </h1>
          <p className="text-xl italic mb-6" style={{ color: "#0F172A" }}>{course.subtitle}</p>
          <hr className="my-6" style={{ borderColor: course.accent }} />
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <Stat label="Lessons" value={`${course.chapters.length}`} accent={course.accent} />
            <Stat label="Total Read Time" value={`~${course.minutes} min`} accent={course.accent} />
            <Stat label="Speaker" value="Mike Boyd" accent={course.accent} />
          </div>
          <div className="rounded-lg p-5 mt-6" style={{ background: `${course.accent}11`, border: `2px solid ${course.accent}55` }}>
            <p className="font-bold text-base mb-2" style={{ color: "#0F172A" }}>How To Use These Scripts</p>
            <ol className="list-decimal list-inside space-y-1.5 text-base leading-relaxed" style={{ color: "#0F172A" }}>
              <li>Read each script aloud once — make it sound like YOU.</li>
              <li>Pencil-mark any phrase you want to change.</li>
              <li>Record each lesson straight through (phone Voice Memos works fine).</li>
              <li>If you stumble, just pause and start the sentence over — we'll cut it clean.</li>
              <li>Send the MP3 files in the chat — they'll wire to the right lessons automatically.</li>
            </ol>
          </div>
        </div>

        {/* ── One page per script ── */}
        {lessons.map((l) => (
          <div key={l.n} className="script-card mb-12 print:mb-0">
            <div className="flex items-center gap-4 mb-3">
              <div className="font-serif-display text-5xl font-bold"
                   style={{ color: course.accent }}>
                {String(l.n).padStart(2, "0")}
              </div>
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold"
                     style={{ color: course.accent }}>
                  Lesson {l.n} of {course.chapters.length}
                </div>
                <h2 className="font-serif-display text-3xl leading-tight"
                    style={{ color: "#0F172A", fontWeight: 700 }}>{l.title}</h2>
              </div>
            </div>
            {l.duration && (
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-4"
                   style={{ color: "#0F172A" }}>
                ★ Estimated record time: ~{l.duration} seconds
              </div>
            )}
            <hr className="my-4" style={{ borderColor: `${course.accent}55` }} />

            {l.script ? (
              <p className="leading-[1.95] mb-6"
                 style={{ fontSize: "20px", color: "#0F172A", textAlign: "left" }}>
                {l.script}
              </p>
            ) : (
              <div className="rounded-lg p-6 italic"
                   style={{ background: "#FFFFFF", border: "2px dashed #2E9F3A", color: "#0F172A" }}>
                Script for this lesson is being written. Check back next session.
              </div>
            )}

            {l.script && (
              <>
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold mt-8 mb-2"
                     style={{ color: course.accent }}>
                  ★ Your Notes
                </div>
                <div className="space-y-5 mb-6">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="border-b" style={{ borderColor: "#0F172A33", height: "24px" }} />
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-lg p-3" style={{ background: `${accent}10`, border: `1.5px solid ${accent}44` }}>
      <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-0.5" style={{ color: accent }}>{label}</div>
      <div className="font-serif-display text-xl" style={{ color: "#0F172A", fontWeight: 700 }}>{value}</div>
    </div>
  );
}
